import React from 'react';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { Illustration } from '@photosi/rn-components/illustration/Illustration';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import braze from 'src/_shared/braze';
import { useStore } from 'src/contexts/StoreContext';
import { ILLUSTRATION_NAMES } from '@photosi/rn-components/illustration/illustration.helper';
import { pickImages } from 'src/store/_shared/imagePickerUtils';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { SELECTED_MENU_ITEMS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { stylesDesktop, stylesMobile } from './PickerComputer.style';
import { SourcesEventType } from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { PickerProps } from '../Picker.types';

export const PickerComputer = observer(
  ({ isDragging, setUploadingPhoto, onConfirmStep, focusGroup, focusId, focusOrder }: PickerProps) => {
    const {
      i18n,
      dynamicModal: { closeModal },
      toastStore,
      project: { activeGroup, replaceUserImage },
      data: { isReplacingPhoto },
    } = useStore();
    const { isDesktop } = useResponsiveScreen();
    const styles = isDesktop ? stylesDesktop : stylesMobile;

    const onPressHandler = async () => {
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceClick, { source_type: SourcesEventType.Computer });
      const productImages: PickedImage[] = await pickImages({
        allowsMultipleSelection: true,
        i18n,
        errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
          if (areAllPhotosInvalid) {
            closeModal();
          }
          toastStore.showErrorToast({
            id: 'alert-uploading-photos-error-toast',
            message: errorMessage,
          });
        },
        newRoute: true,
      });
      if (!!productImages.length) {
        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick, {
          source_type: SourcesEventType.Computer,
        });
        braze.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick);
        setUploadingPhoto && setUploadingPhoto(true);
        productImages.forEach(productImage => (productImage.source = SELECTED_MENU_ITEMS.Computer));
        if (!isReplacingPhoto) {
          onConfirmStep(false, productImages);
        } else {
          const image = activeGroup?.userImagesWithQualityAlert.find(e => e.id === isReplacingPhoto);
          image &&
            replaceUserImage({
              pickedImage: productImages[0],
              googleAccessToken: '',
              productId: image?.productId,
              userImageId: image?.id,
            });
        }

        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
          n_photo: productImages.length,
          source_type: SourcesEventType.Computer,
        });
        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.continueClick, {
          n_photo: productImages.length,
          n_photos_facebook: 0,
          n_photos_gphoto: 0,
          n_photos_local: productImages.length,
          n_photos_smartphone: 0,
          n_photos_family_friends: 0,
        });
        setUploadingPhoto && setUploadingPhoto(false);
      }
    };

    return (
      <View style={styles.details}>
        <View style={{ width: 54, height: 54, opacity: isDragging ? 0.3 : 1 }}>
          <Illustration name={ILLUSTRATION_NAMES.Import} />
        </View>
        <View style={styles.textWrapper}>
          <TextLato style={styles.title}>
            {isDesktop ? i18n.t('upload:upload-computer-title') : i18n.t('upload:upload-gallery-title')}
          </TextLato>
          {isDesktop && <TextLato style={styles.description}>{i18n.t('upload:upload-computer-description')}</TextLato>}
        </View>
        <PrimaryButton
          style={styles.button}
          layout={BUTTON_LAYOUTS.CENTER}
          innerStyle={{ opacity: isDragging ? 0.3 : 1 }}
          onPress={onPressHandler}
          testID={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.CHOOSE)}
          focusId={focusId}
          focusGroup={focusGroup}
          focusOrder={focusOrder}
          text={isDesktop ? i18n.t('upload:upload-from-computer') : i18n.t('upload:choose')}
          size={BUTTON_SIZES.REGULAR}
        />
      </View>
    );
  },
);
